// A small dialog hung off the key that opened it. The trigger, its tooltip and the open state live
// here; the panel is the kit Popover, which draws on <body> so a table's clipping shell never cuts it.

import React, { useRef, useState } from 'react';
import KitPopover from '../components/ui/Popover';
import Tooltip from '../components/ui/Tooltip';

// React 17: no useId.
let seq = 0;

export interface PopoverProps {
  triggerLabel: React.ReactNode;
  triggerClassName: string;
  /** Hover text saying what happens, shown by the kit Tooltip around the trigger. */
  tooltip?: string;
  /** Accessible name when the visible label needs more context. */
  triggerAriaLabel?: string;
  title: string;
  disabled?: boolean;
  /** Which trigger edge the panel lines up with. */
  align?: 'left' | 'right';
  /** sm 16rem, md 20rem (default). */
  width?: 'sm' | 'md';
  onOpen?: () => void;
  children: (close: () => void) => React.ReactNode;
}

const Popover: React.FC<PopoverProps> = ({
  triggerLabel,
  triggerClassName,
  tooltip,
  triggerAriaLabel,
  title,
  disabled,
  align = 'left',
  width = 'md',
  onOpen,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const idRef = useRef('');
  if (!idRef.current) idRef.current = `wo-popover-${++seq}`;

  return (
    <>
      <Tooltip label={tooltip ?? ''}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-label={triggerAriaLabel}
          aria-expanded={open}
          aria-controls={open ? idRef.current : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (open) setOpen(false);
            else {
              setOpen(true);
              onOpen?.();
            }
          }}
          className={triggerClassName}
        >
          {triggerLabel}
        </button>
      </Tooltip>
      {/* React events bubble out of a portal: without this a click in the panel would reach the row it hangs off. */}
      <span className="contents" onClick={(e) => e.stopPropagation()}>
        <KitPopover
          open={open}
          onClose={() => setOpen(false)}
          anchor={triggerRef}
          title={title}
          align={align === 'right' ? 'end' : 'start'}
          width={width}
          id={idRef.current}
        >
          {children(() => setOpen(false))}
        </KitPopover>
      </span>
    </>
  );
};

export default Popover;
